import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { trackEvent } from "../analytics";
import {
  acceptBriefingInvite,
  fetchInviteLanding,
  fetchMyInviteLink,
} from "../api/briefingInvites";
import { useAuth } from "../contexts/AuthContext";
import { getInstallPromptMode, isIosDevice } from "../lib/installPrompt";
import { openInstallPrompt, useInstallPrompt } from "../hooks/useInstallPrompt";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { isStandaloneDisplay } from "../lib/pushNotifications";

function briefingTextFromSections(sections) {
  return (sections || [])
    .filter((s) => s && s.text)
    .map((s) => `${s.label}: ${s.text}`)
    .join(" ");
}

export default function BriefingInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { canNativeInstall, promptInstall } = useInstallPrompt();
  const {
    supported: pushSupported,
    subscribed: pushSubscribed,
    loading: pushLoading,
    error: pushError,
    enablePush,
  } = usePushNotifications({ enabled: Boolean(user) });

  const [landing, setLanding] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState(null);
  const [installBusy, setInstallBusy] = useState(false);

  const standalone = isStandaloneDisplay();
  const pushNeedsInstall = isIosDevice() && !standalone;
  const installMode = getInstallPromptMode(canNativeInstall);
  const invitePath = `/einladung/${token}`;

  useEffect(() => {
    if (!token) return;
    let active = true;
    fetchInviteLanding(token)
      .then((data) => {
        if (!active) return;
        setLanding(data);
        trackEvent("briefing_invite_open");
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(err.message || "Einladung konnte nicht geladen werden.");
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (authLoading || !user || !token) return;
    let active = true;
    fetchMyInviteLink()
      .then((mine) => {
        if (!active) return;
        if (mine.token === token) {
          navigate("/", { replace: true });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [authLoading, user, token, navigate]);

  const handleActivate = useCallback(async () => {
    if (!user || !token) return;
    setActivating(true);
    setActivateError(null);
    try {
      await acceptBriefingInvite(token);
      trackEvent("briefing_invite_activate");
      navigate("/", { replace: true });
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : String(err));
    } finally {
      setActivating(false);
    }
  }, [user, token, navigate]);

  const handleInstall = async () => {
    if (installMode === "native") {
      setInstallBusy(true);
      try {
        await promptInstall();
      } finally {
        setInstallBusy(false);
      }
      return;
    }
    openInstallPrompt();
  };

  const handleEnablePush = async () => {
    try {
      await enablePush();
    } catch {
      /* hook sets error */
    }
  };

  if (loadError) {
    return (
      <div className="login-page invite-page">
        <div className="login-card invite-card">
          <h1 className="login-title">Einladung</h1>
          <p className="login-error">{loadError}</p>
          <p className="login-back">
            <Link to="/">Zur Startseite</Link>
          </p>
        </div>
      </div>
    );
  }

  if (!landing) {
    return (
      <div className="login-page invite-page">
        <div className="login-card invite-card">
          <p className="login-loading">Wird geladen…</p>
        </div>
      </div>
    );
  }

  const briefing = landing.briefing;
  const previewText =
    briefing?.chatText || briefingTextFromSections(briefing?.sections);

  return (
    <div className="login-page invite-page">
      <div className="login-card invite-card">
        <h1 className="login-title">Morgen-Briefing</h1>
        <p className="login-subtitle">
          {landing.inviterFirstName} nutzt Albis für eure Termine und das Morgen-Briefing — so
          sieht es heute aus:
        </p>

        {previewText ? (
          <section
            className="heute-morning-briefing invite-briefing-preview"
            aria-label="Heutiges Briefing"
          >
            <p className="invite-briefing-chat">{previewText}</p>
            {briefing?.sections ? (
              <ul className="heute-morning-briefing-list">
                {briefing.sections
                  .filter((s) => s && s.text)
                  .map((s) => (
                    <li key={s.id} className="heute-morning-briefing-item">
                      <span className="heute-morning-briefing-label">{s.label}</span>
                      <span className="heute-morning-briefing-text">{s.text}</span>
                    </li>
                  ))}
              </ul>
            ) : null}
          </section>
        ) : null}

        {!user ? (
          <>
            <Link
              to={`/login?redirect=${encodeURIComponent(invitePath)}`}
              className="add-card-btn add-card-btn-primary invite-cta-btn"
            >
              Briefing aktivieren
            </Link>
            <p className="invite-step-hint">
              Mit Google anmelden — danach siehst du KiTa-Termine und Fristen
              gemeinsam, und das Morgen-Briefing.
            </p>
          </>
        ) : (
          <div className="invite-activation-steps">
            <h2 className="invite-steps-title">Fast geschafft</h2>
            <ul className="invite-checklist">
              <li className="invite-checklist-item invite-checklist-item--done">
                Angemeldet
              </li>
              {pushNeedsInstall ? (
                <li className="invite-checklist-item">
                  <span>Albis zum Home-Bildschirm</span>
                  <button
                    type="button"
                    className="mehr-push-btn mehr-push-btn-secondary"
                    onClick={handleInstall}
                    disabled={installBusy}
                  >
                    {installBusy ? "Wird geöffnet…" : "Anleitung anzeigen"}
                  </button>
                  {installMode === "ios" ? (
                    <p className="invite-step-hint">
                      Teilen → Zum Home-Bildschirm → Hinzufügen. Danach Albis
                      von dort öffnen.
                    </p>
                  ) : null}
                </li>
              ) : standalone ? (
                <li className="invite-checklist-item invite-checklist-item--done">
                  App installiert
                </li>
              ) : null}
              {pushSupported && !pushSubscribed ? (
                <li className="invite-checklist-item">
                  <span>Erinnerungen für das Morgen-Briefing</span>
                  <button
                    type="button"
                    className="mehr-push-btn"
                    onClick={handleEnablePush}
                    disabled={pushLoading || pushNeedsInstall}
                  >
                    {pushLoading
                      ? "Wird eingerichtet…"
                      : pushNeedsInstall
                        ? "Zuerst installieren"
                        : "Erinnerungen aktivieren"}
                  </button>
                  {pushNeedsInstall ? (
                    <p className="invite-step-hint">
                      Erinnerungen funktionieren, wenn du Albis vom Home-Bildschirm
                      startest.
                    </p>
                  ) : null}
                  {pushError ? (
                    <p className="login-error">{pushError}</p>
                  ) : null}
                </li>
              ) : pushSubscribed ? (
                <li className="invite-checklist-item invite-checklist-item--done">
                  Erinnerungen aktiv
                </li>
              ) : null}
            </ul>

            {activateError ? <p className="login-error">{activateError}</p> : null}

            <button
              type="button"
              className="add-card-btn add-card-btn-primary invite-cta-btn"
              onClick={handleActivate}
              disabled={activating}
            >
              {activating ? "Wird aktiviert…" : "Briefing starten"}
            </button>
            <p className="invite-step-hint">
              KiTa-Termine und Fristen seht ihr beide — private Sachen bleiben
              beim anderen. Das Morgen-Briefing findest du ab sofort im Chat.
            </p>
          </div>
        )}

        <p className="login-back">
          <Link to="/">Zur Startseite</Link>
        </p>
      </div>
    </div>
  );
}
